"use strict";
import { useMemo } from "react";
import { useUserTokens } from "contexts/UserTokensContext";
import { useTokenLists } from "contexts/TokenListsContext";
import { getCommonBase } from "constants/uniswapRouting";
import { getDexTokenList } from "constants/topazTokenList";
import { SafetyLevel } from "data/graphql/uniswap-data-api/__generated__/types-and-hooks";
import { getCurrencySafetyInfo } from "features/dataApi/utils";
import { getTokenLogoUrl } from "constants/tokenLogos";
import {
  buildNativeCurrencyId,
  buildWrappedNativeCurrencyId,
  currencyIdToAddress,
  currencyIdToChain,
  isNativeCurrencyAddress
} from "utils/uniswapCurrencyId";
export function useCurrencyInfo(_currencyId, options) {
  const { userAddedTokens } = useUserTokens();
  const { getTokenListToken, getTokenListLogoUrl } = useTokenLists();
  return useMemo(() => {
    if (!_currencyId || options?.skip) {
      return void 0;
    }
    const chainId = currencyIdToChain(_currencyId);
    let address;
    try {
      address = currencyIdToAddress(_currencyId);
    } catch (error) {
      return void 0;
    }
    if (chainId && address) {
      const resolvedAddress = address;
      const commonBase = getCommonBase(chainId, isNativeCurrencyAddress(chainId, address), address);
      if (commonBase) {
        const copyCommonBase = { ...commonBase };
        copyCommonBase.currencyId = _currencyId;
        return copyCommonBase;
      }
      const dexTokens = getDexTokenList(chainId);
      const dexToken = dexTokens.find(
        (token) => token.address.toLowerCase() === resolvedAddress.toLowerCase()
      );
      if (dexToken) {
        return {
          currency: dexToken,
          currencyId: _currencyId,
          safetyInfo: getCurrencySafetyInfo(SafetyLevel.Verified),
          logoUrl: getTokenLogoUrl(address)
        };
      }
      const listToken = getTokenListToken(chainId, address);
      if (listToken) {
        return {
          currency: listToken.token,
          currencyId: _currencyId,
          // Tokens from our curated/extended token lists are trusted — no safety warning.
          safetyInfo: getCurrencySafetyInfo(SafetyLevel.Verified),
          logoUrl: listToken.logoURI ?? getTokenListLogoUrl(address)
        };
      }
      if (userAddedTokens) {
        const userToken = userAddedTokens.find(
          (token) => token.chainId === chainId && token.address.toLowerCase() === resolvedAddress.toLowerCase()
        );
        if (userToken) {
          return {
            currency: userToken,
            currencyId: _currencyId,
            safetyInfo: getCurrencySafetyInfo(SafetyLevel.MediumWarning),
            logoUrl: getTokenLogoUrl(address) ?? getTokenListLogoUrl(address)
          };
        }
      }
    }
    return void 0;
  }, [_currencyId, options?.skip, userAddedTokens, getTokenListToken, getTokenListLogoUrl]);
}
export function useCurrencyInfos(_currencyIds, options) {
  const { userAddedTokens } = useUserTokens();
  const { getTokenListToken, getTokenListLogoUrl } = useTokenLists();
  return useMemo(() => {
    if (!_currencyIds.length || options?.skip) {
      return [];
    }
    return _currencyIds.map((currencyId) => {
      if (!currencyId) {
        return void 0;
      }
      const chainId = currencyIdToChain(currencyId);
      let address;
      try {
        address = currencyIdToAddress(currencyId);
      } catch (error) {
        return void 0;
      }
      if (chainId && address) {
        const resolvedAddress = address;
        const commonBase = getCommonBase(chainId, isNativeCurrencyAddress(chainId, address), address);
        if (commonBase) {
          const copyCommonBase = { ...commonBase };
          copyCommonBase.currencyId = currencyId;
          return copyCommonBase;
        }
        const dexTokens = getDexTokenList(chainId);
        const dexToken = dexTokens.find(
          (token) => token.address.toLowerCase() === resolvedAddress.toLowerCase()
        );
        if (dexToken) {
          return {
            currency: dexToken,
            currencyId,
            safetyInfo: getCurrencySafetyInfo(SafetyLevel.Verified),
            logoUrl: getTokenLogoUrl(address)
          };
        }
        const listToken = getTokenListToken(chainId, address);
        if (listToken) {
          return {
            currency: listToken.token,
            currencyId,
            // Tokens from our curated/extended token lists are trusted — no safety warning.
            safetyInfo: getCurrencySafetyInfo(SafetyLevel.Verified),
            logoUrl: listToken.logoURI ?? getTokenListLogoUrl(address)
          };
        }
        if (userAddedTokens) {
          const userToken = userAddedTokens.find(
            (token) => token.chainId === chainId && token.address.toLowerCase() === resolvedAddress.toLowerCase()
          );
          if (userToken) {
            return {
              currency: userToken,
              currencyId,
              safetyInfo: getCurrencySafetyInfo(SafetyLevel.MediumWarning),
              logoUrl: getTokenLogoUrl(address) ?? getTokenListLogoUrl(address)
            };
          }
        }
      }
      return void 0;
    });
  }, [_currencyIds, options?.skip, userAddedTokens, getTokenListToken, getTokenListLogoUrl]);
}
export function useNativeCurrencyInfo(chainId) {
  const nativeCurrencyId = buildNativeCurrencyId(chainId);
  return useCurrencyInfo(nativeCurrencyId);
}
export function useWrappedNativeCurrencyInfo(chainId) {
  const wrappedCurrencyId = buildWrappedNativeCurrencyId(chainId);
  return useCurrencyInfo(wrappedCurrencyId);
}
